import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';

export interface HorarioRango {
  inicio: string;
  fin: string;
}

interface HolidayTimeRangesProps {
  value: HorarioRango[] | null;
  onChange: (value: HorarioRango[] | null) => void;
}

export const HolidayTimeRanges: React.FC<HolidayTimeRangesProps> = ({ value, onChange }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newInicio, setNewInicio] = useState('08:00');
  const [newFin, setNewFin] = useState('14:00');
  const [error, setError] = useState<string | null>(null);

  const ranges = value || [];
  const maxRanges = 6;

  const handleAddRange = () => {
    if (!newInicio || !newFin) {
      setError('Debe especificar hora de inicio y fin');
      return;
    }

    if (newInicio >= newFin) {
      setError('La hora de inicio debe ser menor que la hora de fin');
      return;
    }

    if (ranges.some(r => 
      (newInicio >= r.inicio && newInicio < r.fin) ||
      (newFin > r.inicio && newFin <= r.fin) ||
      (newInicio <= r.inicio && newFin >= r.fin)
    )) {
      setError('Este rango se solapa con un rango existente');
      return;
    }

    const newRanges = [...ranges, { inicio: newInicio, fin: newFin }]
      .sort((a, b) => a.inicio.localeCompare(b.inicio));

    onChange(newRanges);
    setNewInicio('08:00');
    setNewFin('14:00');
    setShowAddForm(false);
    setError(null);
  };

  const handleRemoveRange = (index: number) => {
    const newRanges = ranges.filter((_, i) => i !== index);
    onChange(newRanges.length > 0 ? newRanges : null);
  };

  const handleClearAll = () => {
    onChange(null);
    setShowAddForm(false);
    setError(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {ranges.length === 0 
            ? 'Día festivo completo (sin horarios especiales)' 
            : `${ranges.length} horario${ranges.length > 1 ? 's' : ''} especial${ranges.length > 1 ? 'es' : ''} definido${ranges.length > 1 ? 's' : ''}`
          }
        </p>
        {ranges.length > 0 && (
          <button
            type="button"
            onClick={handleClearAll}
            className="text-xs text-gray-500 hover:text-red-600 underline"
          >
            Limpiar todo
          </button>
        )}
      </div>

      {ranges.length > 0 && (
        <div className="space-y-2">
          {ranges.map((range, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg"
            >
              <div className="flex-1 font-medium text-sm text-blue-900">
                {range.inicio} - {range.fin}
              </div>
              <button
                type="button"
                onClick={() => handleRemoveRange(index)}
                className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                title="Eliminar rango"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {error}
        </div>
      )}

      {!showAddForm && ranges.length < maxRanges && (
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span className="text-sm font-medium">
            {ranges.length === 0 ? 'Agregar horario especial' : 'Agregar otro horario'}
          </span>
        </button>
      )}

      {showAddForm && (
        <div className="p-4 border-2 border-blue-300 rounded-lg bg-blue-50 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hora inicio
              </label>
              <input
                type="time"
                value={newInicio}
                onChange={(e) => {
                  setNewInicio(e.target.value);
                  setError(null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hora fin
              </label>
              <input
                type="time"
                value={newFin}
                onChange={(e) => {
                  setNewFin(e.target.value);
                  setError(null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleAddRange}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Agregar
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setError(null);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {ranges.length >= maxRanges && (
        <p className="text-xs text-gray-500 text-center">
          Máximo {maxRanges} rangos de horario permitidos
        </p>
      )}
    </div>
  );
};
